import { DEFAULT_DATE_TIME_RANGE } from '@modules/common/components/DateRangePicker/constants';
import { mapToTimePeriodUnit } from '@modules/common/utils';
import { isSuperUserType } from '@modules/users/utils';
import dayjs from 'dayjs';
import {
  TenantTier as ETenantTier,
  UserRole as EUserRole,
  UserType as EUserTYpe,
  PermissionOperation,
  TenantViewFragment,
  UserViewFragment,
} from 'generated/graphql';
import { dateObjFromUnixWithTZ, dayjsTz } from 'v2/core/date/date';
import {
  BaseUser,
  SuperPermissionResource,
  Tenant,
  TenantTier,
  User,
  UserPermissionType,
  UserRole,
  UserType,
} from 'v2/core/entities/user';
import { mapToDashboardListItems } from 'v2/core/repository/edge-server/dashboard/useListDashboards';

export type EBaseUser = Omit<UserViewFragment, 'Tenant'>;
export type UserWithTenant = UserViewFragment;

export const MapToBaseUserEntity = (user: EBaseUser): BaseUser => {
  const userType = MapToUserType(user.type);

  return {
    id: user.id,
    email: user.email,
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    type: userType,
    role: MapToUserRole(user.role),
    createdAt: dayjs.unix(user.createdAt),
    notificationChannels: user.notificationChannels,
    interests: {
      keywords: user.interests?.keywords,
      labelIDs: user.interests?.labelIDs,
    },
    pinnedDashboards: user.pinnedDashboards,
    state: user.state,
    intercomUserHash: user.intercomUserHash,
    permissions: MapToUserPermissions(user.permissions, userType),
  };
};

export const MapToUserPermissions = (
  edgePermissions: EBaseUser['permissions'],
  userType: UserType,
): UserPermissionType[] => {
  const permissions: UserPermissionType[] = [...(edgePermissions ?? [])];
  const isSuperUser = isSuperUserType(userType);

  if (!isSuperUser) {
    return permissions;
  }

  return [
    ...permissions,
    {
      resource: SuperPermissionResource.TenantSwitch,
      operation: PermissionOperation.Write,
    },
    {
      resource: SuperPermissionResource.Discoverability,
      operation: PermissionOperation.Write,
    },
  ];
};

export const MapToUserEntity = (user: UserWithTenant): User => ({
  ...MapToBaseUserEntity(user),
  tenant: MapToTenant(user.Tenant),
});

export const MapToUserRole = (
  userRole: EUserRole | undefined | null,
): UserRole => {
  switch (userRole) {
    case EUserRole.ProductManagement:
      return UserRole.ProductManagement;
    case EUserRole.ProductOperation:
      return UserRole.ProductOperation;
    case EUserRole.UxResearch:
      return UserRole.UxResearch;
    case EUserRole.Founder:
      return UserRole.Founder;
    case EUserRole.UserResearch:
      return UserRole.UserResearch;
    case EUserRole.CustomerSupport:
      return UserRole.CustomerSupport;
    case EUserRole.Other:
      return UserRole.Other;
    default:
      return UserRole.Unknown;
  }
};

export const MapToUserType = (userType: EUserTYpe): UserType => {
  switch (userType) {
    case EUserTYpe.Admin:
      return UserType.Admin;
    case EUserTYpe.SuperAdmin:
      return UserType.SuperAdmin;
    case EUserTYpe.Viewer:
      return UserType.Viewer;
    case EUserTYpe.SuperViewer:
      return UserType.SuperViewer;
    case EUserTYpe.Manager:
      return UserType.Manager;
    case EUserTYpe.SuperManager:
      return UserType.SuperManager;
    case EUserTYpe.SuperEditor:
      return UserType.SuperEditor;
    case EUserTYpe.Editor:
      return UserType.Editor;
    default:
      return UserType.Unknown;
  }
};

export const MapToTenant = (tenant: TenantViewFragment): Tenant => {
  if (!tenant.slug) {
    throw Error('tenant slug not defined ' + JSON.stringify(tenant));
  }
  const timezone = tenant.timezone ?? dayjsTz.tz.guess();
  return {
    id: tenant.id,
    name: tenant.name,
    tier: MapToTenantTier(tenant.tier),
    timezone,
    avatarURL: tenant.avatarURL,
    slug: tenant.slug.replace('.enterpret.com', ''),
    dashboardSettings: mapDashboardSettings(tenant.dashboardSettings, timezone),
    pinnedDashboards: mapToDashboardListItems(tenant.pinnedDashboards ?? []),
    domains: tenant.domains,
    state: tenant.state,
  };
};

const mapDashboardSettings = (
  settings: TenantViewFragment['dashboardSettings'],
  timezone: string,
): Tenant['dashboardSettings'] => {
  if (!settings) {
    return {
      showUnTranslatedRecords: false,
      defaultTimeRange: DEFAULT_DATE_TIME_RANGE,
      startDate: dayjsTz.tz('01/01/2021', timezone),
    };
  }

  return {
    startDate: dateObjFromUnixWithTZ(settings.maxLookbackDate)!,
    defaultTimeRange: settings.defaultTimeRange
      ? {
          type: 'last',
          value: settings.defaultTimeRange.value,
          unit: mapToTimePeriodUnit(settings.defaultTimeRange.unit),
        }
      : undefined,
    homePageStatsDashboard: settings.homePageStatsDashboard,
    showUnTranslatedRecords: settings.showUnTranslatedRecords,
  };
};

export const MapToTenantTier = (tenantTier: ETenantTier): TenantTier => {
  switch (tenantTier) {
    case ETenantTier.Basic:
      return TenantTier.Basic;
    case ETenantTier.Premium:
      return TenantTier.Premium;
    case ETenantTier.Freemium:
      return TenantTier.Freemium;
  }
};
